/**
 * Traffic Logger - Web App
 * 
 * Loggar sidvisningar till API för trafikanalys.
 * Kör i bakgrunden och ska aldrig blockera användarupplevelsen.
 */

import { getApiUrl } from './api';

interface TrafficEvent {
  app: 'web' | 'blog' | 'admin';
  path: string;
  sessionId?: string;
  referrer?: string;
}

// Generate or retrieve session ID for tracking sessions
let sessionId: string | undefined;

function getSessionId(): string {
  if (!sessionId) {
    // Try to get from sessionStorage
    const stored = sessionStorage.getItem('traffic_session_id');
    if (stored) {
      sessionId = stored;
    } else {
      // Generate new session ID
      sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
      sessionStorage.setItem('traffic_session_id', sessionId);
    }
  }
  return sessionId;
}

/**
 * Log page view for traffic analytics.
 * Runs in background, does not block user experience.
 * Silently fails if API is unavailable.
 */
export async function logPageView(event: TrafficEvent): Promise<void> {
  // Skip in development if preferred (can be configured)
  const skipInDev = import.meta.env.VITE_SKIP_TRAFFIC_LOGGING === 'true';
  if (skipInDev && import.meta.env.DEV) {
    console.log('[Traffic] Skipping (VITE_SKIP_TRAFFIC_LOGGING=true)');
    return;
  }

  try {
    const url = getApiUrl('traffic/log');
    const payload = {
      ...event,
      sessionId: getSessionId(),
      referrer: document.referrer || undefined,
    };

    // Log in development for debugging
    if (import.meta.env.DEV) {
      console.log('[Traffic] Logging:', payload);
    }

    // Don't await - fire and forget
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      // Don't include credentials - this should work for anonymous users
      // Use keepalive to ensure request completes even if user navigates away
      keepalive: true,
    }).catch((error) => {
      // Log errors in development
      if (import.meta.env.DEV) {
        console.error('[Traffic] Failed to log:', error);
      }
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('[Traffic] Error:', error);
    }
  }
}

/**
 * Hook version for easier use in React components
 */
export function useTrafficLogger(app: 'web' | 'blog' | 'admin') {
  return (path: string) => {
    logPageView({ app, path });
  };
}
